import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogIn } from "lucide-react";
import { NAV } from "@/data/content";
import ThemeToggle from "@/components/ThemeToggle";
import { track } from "@/lib/api";

const Logo = () => (
  <Link to="/" className="flex items-center gap-2" data-testid="nav-logo" aria-label="Amaya Pro home">
    <img src="/brand/logo-black.png" alt="Amaya Pro" className="h-7 w-auto dark:hidden" />
    <img src="/brand/logo-white.png" alt="" className="h-7 w-auto hidden dark:block" aria-hidden="true" />
  </Link>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 border-b ${
        scrolled || open ? "bg-white/90 backdrop-blur-xl border-zinc-200" : "bg-white/75 backdrop-blur-lg border-zinc-200/70"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
          <ThemeToggle />
        </div>
        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${isActive ? "text-ink font-medium" : "text-zinc-500 hover:text-ink"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contact?type=quote"
            data-testid="nav-quote-btn"
            onClick={() => track("cta_click", { cta: "get_quote", location: "navbar" })}
            className="text-sm px-4 py-2 border border-zinc-300 text-ink hover:border-signal transition-colors whitespace-nowrap"
          >
            Get a Quote
          </Link>
          <a
            href="https://mes.amaya.energy/login"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-partner-login-btn"
            className="text-sm px-4 py-2 bg-signal text-white font-medium hover:bg-[#5c8f23] transition-colors flex items-center gap-1.5 whitespace-nowrap"
          >
            <LogIn size={14} /> Partner Login
          </a>
        </div>
        <button
          className="lg:hidden text-ink p-2"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          data-testid="mobile-menu-btn"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden border-t border-zinc-200 bg-white/95 backdrop-blur-xl"
            aria-label="Mobile"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} className="text-lg text-zinc-600 hover:text-ink" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>
                  {n.label}
                </NavLink>
              ))}
              <div className="flex gap-3 pt-4 items-center">
                <a href="https://mes.amaya.energy/login" target="_blank" rel="noopener noreferrer" className="flex-1 text-center px-3 py-3 bg-signal text-white text-sm font-medium flex items-center justify-center gap-1.5 whitespace-nowrap" data-testid="mobile-partner-login-btn">
                  <LogIn size={14} /> Partner Login
                </a>
                <Link to="/contact?type=quote" className="flex-1 text-center px-3 py-3 border border-zinc-300 text-ink text-sm whitespace-nowrap" data-testid="mobile-quote-btn">
                  Get a Quote
                </Link>
                <Link to="/contact?type=demo" className="flex-1 text-center px-3 py-3 border border-zinc-300 text-ink text-sm whitespace-nowrap" data-testid="mobile-demo-btn">
                  Demo
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
